import { type ExpandedStateList } from '@tanstack/react-table';
import { type JSX } from 'react';

import Spacings from '@dynatrace/strato-design-tokens/spacings';

import type { DataTableSubRowsProps } from './sub-rows-types.js';
import { calculateMaxVisibleDepth, isRowDisabled } from './sub-rows-utils.js';
import { useControllableState } from '../../../../core/hooks/useControllableState.js';
import {
  DATA_TABLE_SUB_ROW_EXPANDER_ICON_SIZE as SUB_ROW_EXPANDER_ICON_SIZE,
  DATA_TABLE_SUB_ROW_INDENT_GRID as SUB_ROW_INDENT_GRID,
  DATA_TABLE_SUB_ROW_DENSITY_PADDING_MAP as SUB_ROW_DENSITY_PADDING_MAP,
} from '../../constants.js';
import { useDataTable } from '../../contexts/DataTableContext.js';
import { makeAccessorFunction } from '../../hooks/makeAccessorFunction.js';
import type {
  ColumnDef,
  Row,
  Table,
  TableFeature,
  TableOptions,
} from '../../hooks/useTable/types.js';
import {
  assignRowMethods,
  getMemoOptions,
} from '../../hooks/useTable/utils.js';
import type { DataTableRowData } from '../../public.api.js';
import { memo } from '../../tanstack-table-core-override/src/utils.js';

/**
 * Default accessor for the subRows
 * @internal
 */
export function defaultSubRowsAccessor<TData>(originalRow: TData): TData[] {
  return (originalRow as TData & { subRows: TData[] })?.subRows;
}

/**
 * @internal
 */
export const DataTableSubRows: TableFeature = {
  createTable<TData extends DataTableRowData>(table: Table<TData>): void {
    const disabledSubRowsCache = new Map<string, boolean>();
    const rowLineConfig = new Map<
      string,
      Array<'Line' | 'TShape' | 'LShape' | 'Empty'>
    >();

    table.getIsAllRowsExpanded = memo(
      () => [table.getState().expanded, table.getRowModel().flatRows],
      (expandedState, flatRows) => {
        // If expandedState is true, save some cycles and return true.
        if (typeof expandedState === 'boolean') {
          return expandedState === true;
        }

        // If expandedState is an object, but has no values, return false immediately
        if (!Object.keys(expandedState).length) {
          return false;
        }

        // If any row that can be expanded is not expanded, return false
        if (
          // The original code from @tanstack/react-table does not check if a row is even
          // expandable before it counts it as isExpanded.
          flatRows.some((row) => {
            return (
              row.getCanExpand() &&
              !row.getIsExpanded() &&
              !row.getCanExpandDisabled() &&
              !table.getIsAncestorDisabled(row)
            );
          })
        ) {
          return false;
        }

        // They must all be expanded :shrug:
        return true;
      },
      getMemoOptions(table.options, 'debugTable', 'table.getIsAllRowsExpanded'),
    );

    /**
     * We have to overwrite the function from tanstack expanding all sub-rows should
     * call the `onOpenSubRowsChange` with all sub-rows and not only `true`.
     */
    table.toggleAllRowsExpanded = () => {
      if (!table.getIsAllRowsExpanded()) {
        const newExpandedState: Record<string, boolean> = table
          .getExpandedRowModel()
          .flatRows.reduce((expanded, row) => {
            if (row.getCanExpand() && !row.getCanExpandDisabled()) {
              expanded[row.id] = true;
            }
            return expanded;
          }, {} as Record<string, boolean>);
        table.setExpanded(newExpandedState);
      } else {
        table.setExpanded({});
      }
    };

    table.getSubRowIndicatorColumnId = memo(
      () => [
        table.options.enableExpanding,
        table.options.subRowColumnId,
        table.getState().columnVisibility,
        table.options.columns,
      ],
      (enableSubRows, subrowColumnId) => {
        if (enableSubRows !== true) {
          return null;
        }

        // If a subrowColumnId is defined in the options, we can immediately
        // use the configured id and do not need to look for the first visible one.
        if (subrowColumnId) {
          table.remeasureColumn(subrowColumnId);
          return subrowColumnId;
        }

        const visibleLeafColumns = table.getVisibleLeafColumns();

        for (const column of visibleLeafColumns) {
          const columnDef = column.columnDef as ColumnDef<TData>;
          if (columnDef.meta && columnDef.meta.builtin !== true) {
            table.remeasureColumn(column.id);
            return column.id;
          }
        }
        return null;
      },
      getMemoOptions(
        table.options,
        'debugTable',
        'table.getSubRowIndicatorColumnId',
      ),
    );

    table.getSubRowOpenDepth = memo(
      () => [table.getState().expanded],
      (expandedState) => {
        const expandedRowIds: string[] = [];

        if (expandedState === true) {
          const rows = table.getExpandedRowModel().rows;

          // all rows are expanded
          for (const row of rows) {
            if (row.subRows.length > 0) {
              // filter out leafs, as they would also not be present in the expanded state
              expandedRowIds.push(row.id);
            }
          }
        } else {
          for (const rowId in expandedState) {
            if (expandedState[rowId]) {
              // only include rows which are actually expanded: true
              expandedRowIds.push(rowId);
            }
          }
        }

        return calculateMaxVisibleDepth(
          expandedRowIds,
          table.options.rowIdSeparator,
        );
      },
      getMemoOptions(table.options, 'debugTable', 'table.getSubRowOpenDepth'),
    );

    table.getIsAncestorDisabled = memo(
      (row) => [row, table.getPreExpandedRowModel().flatRows],
      (row) => isRowDisabled(disabledSubRowsCache, row),
      getMemoOptions(
        table.options,
        'debugTable',
        'table.getIsAncestorDisabled',
      ),
    );

    table.getHasSubRows = () =>
      table.getCanSomeRowsExpand() || table.getIsAllRowsExpanded();

    table.getRowLineConfig = memo(
      (rowId) => [rowId],
      (rowId) => {
        if (!rowId) {
          return [];
        }
        const lineConfig: Array<'Line' | 'TShape' | 'LShape' | 'Empty'> = [];
        const row = table.getRow(rowId);

        for (let i = 1; i <= row.depth; i += 1) {
          if (i === row.depth) {
            const isLastExpandedRowOnLevel = row.getIsLastExpandedOnLevel();
            if (isLastExpandedRowOnLevel) {
              lineConfig.push('LShape');
              continue;
            }
            lineConfig.push('TShape');
            continue;
          }
          lineConfig.push('Line');
        }

        // Fill the empty gaps and inherit the ones from the parent as well.
        const parentId = row.parentId;
        if (parentId) {
          const parentShapes =
            rowLineConfig.get(parentId) ?? table.getRowLineConfig(parentId);
          if (parentShapes.at(-1) === 'LShape') {
            lineConfig[lineConfig.length - 2] = 'Empty';
          }
          for (const [index, shape] of parentShapes.entries()) {
            if (shape === 'Empty') {
              lineConfig[index] = 'Empty';
            }
          }
        }

        // Populate the cache with the stored items.
        rowLineConfig.set(rowId, lineConfig);
        return lineConfig;
      },
      getMemoOptions(table.options, 'debugTable', 'table.getRowLineConfig'),
    );

    assignRowMethods(table, {
      toggleExpanded(this: Row<TData>, expanded?: boolean): void {
        table.setExpanded((prev) => {
          // true, if the current row was expanded before
          const exists = prev === true ? true : !!prev?.[this.id];

          let prevExpandedState: ExpandedStateList = {};
          if (prev === true) {
            // ALL rows were expanded before
            table.getCoreRowModel().flatRows.forEach((row) => {
              if (
                !row.getCanExpandDisabled() ||
                !table.getIsAncestorDisabled(row)
              ) {
                prevExpandedState[row.id] = true;
              }
            });
          } else {
            prevExpandedState = prev;
          }

          const shouldExpand = expanded ?? !exists;
          if (!exists && shouldExpand) {
            // row should expand and was not expanded before → add to new expand state
            return {
              ...prevExpandedState,
              [this.id]: true,
            };
          }

          if (exists && !shouldExpand) {
            // row was expanded before and should be collapsed
            for (const key in prevExpandedState) {
              // also collapse all its sub-rows
              if (key.startsWith(this.id)) {
                delete prevExpandedState[key];
              }
            }
            // remove row itself from expand state
            const { [this.id]: _, ...rest } = prevExpandedState;
            return rest;
          }

          // row should not expand and also was not expanded before
          // do nothing and return old state
          return prev;
        });
      },

      getIsLastExpandedOnLevel: memo(
        function (this: Row<TData>) {
          return [
            table.getRowOrderRowModel().flatRows,
            table.getState().expanded,
            this.id,
          ];
        },
        function (this: Row<TData>, orderedFlatRows: Row<TData>[]): boolean {
          const parentRow = this.getParentRow();
          const orderedSiblings = orderedFlatRows.find(
            (row: Row<TData>) => row.id === parentRow?.id,
          )?.subRows;
          const lastSibling = orderedSiblings?.at(-1)?.id;
          return lastSibling === this.id;
        },
        getMemoOptions(
          table.options,
          'debugRows',
          'row.getIsLastInExpandedOnLevel',
        ),
      ),

      getLastExpandedSubRow: memo(
        function (this: Row<TData>) {
          const state = table.getState();
          return [
            state.rowOrder,
            state.sorting,
            state.expanded,
            table.options.data,
            this.id,
          ];
        },
        function (this: Row<TData>) {
          const orderedRow = table
            .getRowOrderRowModel()
            .flatRows.find((sortedRow: Row<TData>) => sortedRow.id === this.id);

          return (
            orderedRow
              ?.getLeafRows()
              // only consider sub-rows that are actually expanded
              .findLast((subRow: Row<TData>) =>
                subRow.getParentRow()?.getIsExpanded(),
              )
          );
        },
        getMemoOptions(table.options, 'debugRows', 'row.getLastExpandedSubRow'),
      ),

      getIsExpanded(this: Row<TData>): boolean {
        const expanded = table.getState().expanded;
        return !!(
          (expanded === true || expanded[this.id]) &&
          !table.getIsAncestorDisabled(this)
        );
      },

      /** Determines whether the given subRow expander is disabled. */
      getCanExpandDisabled(this: Row<TData>): boolean {
        return table.options.getRowExpandDisabled?.(this.original) ?? false;
      },

      getSubRowDepth(this: Row<TData>) {
        return this.id.split(table.options.rowIdSeparator || '.').length - 1;
      },
    });
  },
};

/**
 * Calculates the width for the subrow indicator cell based on the current indent width.
 * @param rowDensity - the configured row density of the table
 * @param indentWidth - the current indent width based on the subrow depth
 * @internal
 */
export function getSubRowCellWidth(
  rowDensity: 'default' | 'condensed' | 'comfortable',
  indentWidth: number,
): string {
  const rowDensityPadding = SUB_ROW_DENSITY_PADDING_MAP[rowDensity];

  // Calculate the cell width by adding the icon button size (icon size + 2 * Size2 padding)
  // and the density padding to the right
  return `calc(${
    indentWidth + SUB_ROW_EXPANDER_ICON_SIZE + rowDensityPadding
  }px + ${Spacings.Size4})`;
}

/**
 * Corrects the subrow header width in situations where content width is not sufficient.
 * This is the case when rows are virtualized or paginated.
 * @internal
 */
export function SubRowsHeaderWidthCorrection(
  props: Readonly<{ subRowOpenDepth: number }>,
): JSX.Element {
  const { subRowOpenDepth } = props;
  const table = useDataTable();
  const hasMultiplePages =
    table.options.enablePagination === true && table.getPageCount() > 1;

  let width: string | undefined;
  if (hasMultiplePages || table.getHasVirtualizedRows()) {
    const indentWidth = SUB_ROW_INDENT_GRID * subRowOpenDepth;
    const rowDensity = table.getVisualVariantOptions().rowDensity;
    width = getSubRowCellWidth(rowDensity, indentWidth);
  }

  return (
    <div
      style={{
        // Expand to fill available space in the cell.
        // This is especially important for right-aligned columns
        // to push the subrow expander all the way to the left.
        flex: 1,
        // Ensure correct width based on the current sub-row max depth,
        // if table has virtualized or paginated rows.
        width,
      }}
    />
  );
}

/**
 * Configuration hook for subrows of the DataTable.
 * @internal
 */
export function useSubRows<TData extends DataTableRowData>(
  props: DataTableSubRowsProps<TData>,
  options: TableOptions<TData>,
): void {
  const { subRows } = props;

  const [openSubRows, setOpenSubRows] = useControllableState({
    value: props.openSubRows,
    defaultValue: props.defaultOpenSubRows,
    onChange: (value) => {
      if (value !== true) {
        props.onOpenSubRowsChange?.(value);
      }
    },
  });

  if (subRows === undefined || subRows === false) {
    options.enableExpanding = false;
    return;
  }

  options.enableExpanding = true;

  // If `subRows` is only set as a boolean prop, we can default to the standard
  // subrow accessor.
  if (typeof subRows === 'boolean') {
    options.getSubRows = defaultSubRowsAccessor;
  } else {
    // If a config object is given, we can handle defaulting here as well
    const { accessor, subRowColumnId: subrowColumnId, disableSubRow } = subRows;

    // Default the accessor if subRows are enabled, but no accessor is given.
    if (accessor === undefined) {
      options.getSubRows = defaultSubRowsAccessor;
    }
    // Allow a simple string accessor for the sub rows.
    else if (typeof accessor === 'string') {
      if (accessor.includes('.') || accessor.includes('[')) {
        const pathAccessFunction = makeAccessorFunction(accessor);
        options.getSubRows = (originalRow) =>
          pathAccessFunction(originalRow) as TData[];
      } else {
        options.getSubRows = (originalRow) =>
          originalRow[accessor as keyof TData] as TData[];
      }
    }
    // If a function is given, we can use this one to get access to the sub rows.
    else if (typeof accessor === 'function') {
      options.getSubRows = (originalRow) => accessor(originalRow);
    }

    // If a custom columnId is given for the injection,
    // we map that into the options to be respected in `getSubRowIndicatorColumnId`
    if (subrowColumnId) {
      options.subRowColumnId = subrowColumnId;
    }

    // If a custom disable configuration is given, we can map that to the
    // getRowCanExpand option and shimming the internal tanstack `Row` object.
    if (disableSubRow) {
      options.getRowExpandDisabled = disableSubRow;
    }
  }

  // Pass the state through to the table.
  options.state = {
    ...options.state,
    expanded: openSubRows ?? {},
  };
  options.onExpandedChange = setOpenSubRows;
}
